import type { GenerationRequestRead, GenerationRequestStatus } from '../types/api';

/**
 * Pipeline status vocabulary, shared by the generation form and the review
 * screen. Status language is fixed here so no screen invents an alternate word
 * for the same state (brand system §7).
 */

/**
 * Every status the backend can report — also the full set of SSE event names,
 * since the event name *is* the status (CONVENTIONS.md).
 */
export const GENERATION_STATUSES: readonly GenerationRequestStatus[] = [
  'queued',
  'research',
  'generating',
  'copy_guardrails',
  'fact_check',
  'awaiting_review',
  'revising',
  'designing',
  'visual_guardrails',
  'awaiting_final_review',
  'complete',
  'failed',
  'rejected',
];

/** Narrows an untrusted string (SSE payload, URL) to a real status. */
export function isGenerationRequestStatus(value: unknown): value is GenerationRequestStatus {
  return typeof value === 'string' && (GENERATION_STATUSES as readonly string[]).includes(value);
}

/**
 * Does this request render anything, and therefore have a visual phase and a
 * Review Step 2? Mirrors the backend rule exactly.
 *
 * A carousel always does. A single image does only when the brief brought no
 * picture of its own — `image_asset_id: null` means "generate the image", and
 * the pipeline draws exactly one slide, which the user approves, rejects or
 * layer-edits like a deck. A single image *with* an asset stops at copy
 * selection, because there is nothing for the design agent to draw.
 * A text-only post never does: the caption is the whole post.
 *
 * Every screen that used to ask `post_type === 'carousel'` about visuals asks
 * this instead; the post type on its own now only decides the wording.
 */
export function hasVisualPhase(
  request: Pick<GenerationRequestRead, 'post_type' | 'image_asset_id'>,
): boolean {
  if (request.post_type === 'text_only') return false;
  return request.post_type === 'carousel' || request.image_asset_id === null;
}

/**
 * A status the server will send nothing further about, so the stream closes.
 *
 * Both waiting states count: the pipeline has stopped and is waiting on a human,
 * which is exactly when the client should stop listening and fetch what landed.
 * `awaiting_review` is terminal for phase one only — choosing a variant on a run
 * that renders something starts phase two and a fresh subscription.
 */
export function isTerminalStatus(status: GenerationRequestStatus): boolean {
  return (
    status === 'awaiting_review' ||
    status === 'awaiting_final_review' ||
    status === 'complete' ||
    status === 'failed' ||
    status === 'rejected'
  );
}

/** Work is still moving: the stage tracker is on screen and SSE should be open. */
export function isInFlightStatus(status: GenerationRequestStatus): boolean {
  return !isTerminalStatus(status);
}

/** One step of the visible pipeline stepper. */
export interface PipelineStage {
  key: string;
  /** Stage-label voice: rendered uppercase in the display face. */
  label: string;
}

/**
 * The five stages a copy-only run walks through, in order. This mirrors real
 * agent state (SRS §3 stages 1-4) — it is not a decorative 1/2/3 sequence.
 *
 * A single image that carries the user's own picture ends here: there is nothing
 * to draw, so the design agent and the visual guardrails are skipped entirely.
 */
export const COPY_PIPELINE_STAGES: readonly PipelineStage[] = [
  { key: 'research', label: 'Research' },
  { key: 'generating', label: 'Write' },
  { key: 'copy_guardrails', label: 'Caption check' },
  { key: 'fact_check', label: 'Fact check' },
  { key: 'awaiting_review', label: 'Your review' },
];

/**
 * The rendering track: the same first five, then the visual phase that only
 * starts once a caption has been chosen (SRS §3 stages 5-7). The design agent
 * renders the chosen variant only, never both. Walked by a carousel and by a
 * single image the user asked us to draw.
 */
export const VISUAL_PIPELINE_STAGES: readonly PipelineStage[] = [
  ...COPY_PIPELINE_STAGES,
  { key: 'designing', label: 'Design' },
  { key: 'visual_guardrails', label: 'Design check' },
  { key: 'awaiting_final_review', label: 'Your approval' },
];

/** The track a request actually walks — see {@link hasVisualPhase}. */
export function pipelineStagesFor(hasVisuals: boolean): readonly PipelineStage[] {
  return hasVisuals ? VISUAL_PIPELINE_STAGES : COPY_PIPELINE_STAGES;
}

/**
 * The one stage that belongs to neither track: rewriting a caption on the
 * reviewer's note.
 *
 * A revision is a detour off a review step, not a step of the pipeline — it
 * starts at a review and comes back to the same one — so the tracker appends it
 * while a run reports `revising` and drops it the moment the run moves on. It
 * never takes a column on a run nobody asked to revise.
 */
export const REVISION_STAGE: PipelineStage = { key: 'revising', label: 'Rewriting' };

/**
 * Position of a status along the full rendering track, whatever this request
 * actually walks.
 *
 * Used to compare two readings of the same request — a stream frame against the
 * status the page loaded with — so a stale frame can never pull the tracker
 * backwards. Every finished state ranks past the last stage.
 *
 * `revising` shares the review position because that is where it starts and
 * where it returns: it sits after the copy phase and before anything visual, so
 * a run revising at step 2 still reports its re-design and re-check frames as
 * forward progress.
 */
export function statusRank(status: GenerationRequestStatus): number {
  switch (status) {
    case 'queued':
      return 0;
    case 'research':
      return 1;
    case 'generating':
      return 2;
    case 'copy_guardrails':
      return 3;
    case 'fact_check':
      return 4;
    case 'awaiting_review':
    case 'revising':
      return 5;
    case 'designing':
      return 6;
    case 'visual_guardrails':
      return 7;
    case 'awaiting_final_review':
      return 8;
    case 'complete':
    case 'failed':
    case 'rejected':
      return 9;
  }
}

/**
 * Which stage of a request's own track a status sits on. `queued` has not
 * started stage one, so it reports -1 and every stage renders as still to come.
 *
 * Finished states sit on the last stage of the track. A phase-two status on a
 * copy-only run cannot happen, but clamps to that track's end rather than
 * reporting an index the tracker cannot render.
 *
 * `revising` is not on either track and reports the review stage it came from;
 * the tracker appends {@link REVISION_STAGE} for it instead of asking here.
 */
export function stageIndexForStatus(status: GenerationRequestStatus, hasVisuals = false): number {
  const lastIndex = pipelineStagesFor(hasVisuals).length - 1;
  if (status === 'queued') return -1;
  return Math.min(statusRank(status) - 1, lastIndex);
}

/**
 * What a status means to someone scanning a list, in five buckets.
 *
 * Lists (history, the operator's recent-requests panel) need a colour and a
 * shape for a status, and the twelve-value vocabulary is too fine for that. The
 * buckets live here with the labels so no screen invents its own mapping.
 */
export type StatusTone = 'running' | 'waiting' | 'cleared' | 'failed' | 'closed';

/** Which bucket a status falls in. */
export function statusTone(status: GenerationRequestStatus): StatusTone {
  switch (status) {
    case 'awaiting_review':
    case 'awaiting_final_review':
      return 'waiting';
    case 'complete':
      return 'cleared';
    case 'failed':
      return 'failed';
    case 'rejected':
      return 'closed';
    default:
      return 'running';
  }
}

/** Sentence-case wording for a status, for prose and mono readouts. */
export const STATUS_LABEL: Readonly<Record<GenerationRequestStatus, string>> = {
  queued: 'Starting',
  research: 'Researching',
  generating: 'Writing',
  copy_guardrails: 'Checking caption',
  fact_check: 'Checking facts',
  awaiting_review: 'Ready for your review',
  revising: 'Rewriting',
  designing: 'Designing',
  visual_guardrails: 'Checking design',
  awaiting_final_review: 'Ready for your approval',
  complete: 'Done',
  failed: 'Failed',
  rejected: 'Rejected',
};
